import { Chart as ChartJS, ChartType } from 'chart.js';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PrintChartContext } from './PrintChartContext';

let chartId = 0;

export const usePrintableChartImage = <TType extends ChartType, TData = unknown>() => {
  const chartRef = useRef<ChartJS<TType, TData> | null>(null);
  const chartIdRef = useRef<string | undefined>(undefined);
  const [imageUrl, setImageUrl] = useState('');
  const { isPrintReport, registerChart, markChartReady } = useContext(PrintChartContext);

  if (!chartIdRef.current) {
    chartId += 1;
    chartIdRef.current = `print-chart-${chartId}`;
  }

  const id = chartIdRef.current;

  useEffect(() => {
    if (!isPrintReport) return;
    registerChart(id);

    return () => markChartReady(id);
  }, [id, isPrintReport, markChartReady, registerChart]);

  useEffect(() => {
    if (!isPrintReport) return;

    let timeout: ReturnType<typeof setTimeout>;
    let animationFrame: number;
    let cancelled = false;

    animationFrame = window.requestAnimationFrame(() => {
      timeout = setTimeout(() => {
        if (cancelled) return;

        const chart = chartRef.current;
        if (!chart) {
          markChartReady(id);
          return;
        }

        (chart.options as { animation?: false }).animation = false;
        chart.resize();
        chart.stop();
        chart.update('none');
        setImageUrl(chart.toBase64Image());
        markChartReady(id);
      }, 100);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(animationFrame);
      clearTimeout(timeout);
    };
  }, [id, isPrintReport, markChartReady]);

  return useMemo(
    () => ({
      chartRef,
      imageUrl,
      isPrintReport
    }),
    [imageUrl, isPrintReport]
  );
};
